import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  ConfirmDialogService,
  DataEntityType,
  isEmptyTrimmed,
  isSomething,
  safeLowerCase,
  SaveForm,
  validateAlphaNum,
} from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { AddCustomCode, DeleteCustomCode, UpdateCustomCode } from '@tv3/store/custom-codes/custom-code.actions'
import { AuthService } from '@tv3/services/auth.service'
import { UpdateCustomCodeRequest } from '@tv3/interfaces/requests/custom-code.request'
import { CustomCode } from '@tv3/store/custom-codes/custom-code.model'
import { selectAllCustomCodeViews, selectRentalsForCode } from '@tv3/store/custom-codes/custom-code.selectors'
import { map, startWith } from 'rxjs/operators'
import { combineLatest, Observable } from 'rxjs'
import * as R from 'ramda'
import { ManageEntityTagsDialogService } from '@tokeet-frontend/tags'

@Component({
  selector: 'app-modify-custom-code',
  templateUrl: './modify-custom-code.component.html',
  styleUrls: ['./modify-custom-code.component.scss'],
})
export class ModifyCustomCodeComponent implements OnInit, OnDestroy {
  code: CustomCode
  tags: string[] = []

  rentals$ = this.store.select(selectRentalsForCode(this.data.code))

  nameMaxLength = 15
  descMaxLength = 140

  form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength), validateAlphaNum()]],
    description: ['', [Validators.maxLength(this.descMaxLength)]],
    rentals: [[]],
    body: ['', [Validators.required, isEmptyTrimmed()]],
  })

  isExisting$: Observable<boolean>

  constructor(
    public dialogRef: MatDialogRef<ModifyCustomCodeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { code: CustomCode },
    private fb: FormBuilder,
    private manageTagsService: ManageEntityTagsDialogService,
    private store: Store<fromRoot.State>,
    private confirmDialogs: ConfirmDialogService,
    private auth: AuthService
  ) {
    this.code = this.data.code
  }

  ngOnInit() {
    if (this.code) {
      this.tags = this.code.attributes.tags
      this.setFormData(this.code)
    } else {
      this.tags = []
    }

    this.isExisting$ = combineLatest(
      this.form.get('name').valueChanges.pipe(startWith('')),
      this.form.get('rentals').valueChanges.pipe(startWith([])),
      this.store.pipe(select(selectAllCustomCodeViews))
    ).pipe(
      map((data) => {
        let [name, rentals, codes] = data

        if (
          this.isEdit() &&
          R.equals(safeLowerCase(name), safeLowerCase(this.code.name)) &&
          R.difference(rentals, this.code.rentals).length === 0 &&
          R.difference(this.code.rentals, rentals).length === 0
        ) {
          return false
        }

        const sameCodes = R.filter((c: CustomCode) => safeLowerCase(c.name) === safeLowerCase(name), codes)

        const filtered = R.filter((c: CustomCode) => {
          return (
            (R.isEmpty(c.rentals) && R.isEmpty(rentals)) ||
            R.any(
              R.equals(true),
              R.map((r) => R.contains(r, c.rentals), rentals)
            )
          )
        }, sameCodes)

        return filtered.length > 0
      })
    )
  }

  ngOnDestroy() {}

  isEdit() {
    return isSomething(this.code) && isSomething(this.code.pkey)
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  save(form: FormGroup) {
    const data = form.getRawValue()
    const code = {
      name: data.name,
      rentals: data.rentals,
      description: data.description,
      body: data.body,
      account: this.auth.user.account,
      creator: this.auth.user.id,
      attributes: { tags: this.tags },
    }

    if (this.code) {
      const editCode: UpdateCustomCodeRequest = { ...code, pkey: this.code.pkey }
      this.store.dispatch(UpdateCustomCode({ request: editCode }))
    } else {
      this.store.dispatch(AddCustomCode({ request: code }))
    }

    this.close()
  }

  onManageTags() {
    this.manageTagsService.open({
      type: DataEntityType.CustomCode,
      id: this.code.pkey,
      tags: this.tags || [],
      title: $localize`Custom Code Tags`,
      description: $localize`This feature allows you to add arbitrary descriptors to this custom code which can then be used when filtering
          on various tables and reports. For example, you can filter by tags on the custom codes table so that you only
          see a subset of all your custom codes`,
      showSuggesttedTags: true,
    })
  }

  setFormData(code: CustomCode) {
    this.form.setValue({
      name: code.name ? code.name : '',
      description: code.description ? code.description : '',
      rentals: code.rentals ? code.rentals : [],
      body: code.body ? code.body : '',
    })
  }

  onRemove() {
    this.confirmDialogs
      .confirm({
        title: $localize`Delete this Custom Code?`,
        body: $localize`Are you sure you want to delete this Custom Code?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteCustomCode({ ids: [this.code.pkey] }))
        this.dialogRef.close()
      })
  }
}
